<script setup lang="ts">
import type { Game, GameClickEventEmit, GameListProps } from '@game/types'
import { computed, nextTick, onBeforeUnmount, onMounted, reactive, ref, useGameConfig, useGameAssets, useTemplateRef, watch, whenever, type Ref, useResizeObserver } from '#imports'

const { translations } = useGameConfig()
const { isVideoTile } = useGameAssets()

const props = withDefaults(defineProps<GameListProps>(), {
    showAllButton: false,
    forceShowAllButton: false,
    showNewGames: true,
    trimDanglingTiles: false,
    hasSidebar: false,
    videoEnabled: true,
    skeletons: 15,
})

const emit = defineEmits<GameClickEventEmit & {
    'load-more-games': [category: string]
}>()

const currentGamesCount = computed(() => props.numberOfGames || props.games?.length)
const gameListBarWidth = computed(() => {
    if (!currentGamesCount.value || !props.totalGames) return 0
    return (currentGamesCount.value / props.totalGames) * 100
})

const firstNewGame: Ref<number[]> = ref([0])

const getGameSetIndex = (index: number) => {
    if (!firstNewGame.value?.length) return 0

    // Find which set this game belongs to by finding the last firstNewGame index that is <= current index
    const setStartIndex = firstNewGame.value.findLast(startIndex => startIndex <= index) || 0

    // Return the index within the current set
    return index - setStartIndex
}

if (import.meta.client) {
    whenever(() => props.games, (newGames) => {
        firstNewGame.value.push(newGames.length)
    })
}

const { trackEvent } = useTracking()
const loadMoreClicks = ref(0)

function loadMoreGames(gamesCategory: string) {
    if (!currentGamesCount.value || !props.totalGames) return

    if (currentGamesCount.value < props.totalGames) {
        emit('load-more-games', props.category)
        loadMoreClicks.value += 1

        trackEvent({
            event_name: `load_more_games`,
            event_type: 'Click',
            meta_data: [{
                category: 'Games',
                gamesCategory,
                totalClicks: loadMoreClicks.value,
                gamesLoaded: currentGamesCount.value,
            }],
            log_entry: true
        }) as void
    }
}

// Check if all games are loaded - used to disable row limiting when complete
const skeletonCount = computed(() => props.skeletons || 0)

const maxVisibleTiles = computed(() => {
    if (props.numberOfGames) return props.numberOfGames
    if (!props.games?.length && props.skeletons) return props.skeletons
    if (!props.trimDanglingTiles) return Infinity

    const maxCols = props.maxColumns || 5

    return maxCols * 3
})

const visibleGamesCount = computed(() => {
    const countGames = props.games?.length || 0

    if (!countGames) return Math.min(skeletonCount.value, maxVisibleTiles.value)

    return Math.min(countGames, maxVisibleTiles.value)
})

const loadedTiles = reactive(new Set<number>())

const visibleTiles = reactive(new Set<number>())
const mediaTiles = reactive(new Set<number>())

// Pre-populate only the first eager SSR tiles; remaining slots lazy-mount when visible.
// Must run during setup (not onMounted) so server and client hydration match.
// Guarded to server + hydration so client-side navigations still lazy-load via IntersectionObserver.
const { isHydrating } = useNuxtApp()
const eagerTiles = (import.meta.server || isHydrating) ? !!(props.games?.length) : false

// Grid is single-row; eager-load the first 8 tiles unless overridden per list.
const eagerTileCount = computed(() => props.eagerTileCount ?? 8)

function isEagerTile(index: number) {
    return eagerTiles && index < eagerTileCount.value
}

if (eagerTiles) {
    for (let i = 0; i < props.games!.length; i++) {
        if (!isEagerTile(i)) continue

        visibleTiles.add(i)
        loadedTiles.add(i)
        mediaTiles.add(i)
    }
}

let observer: IntersectionObserver | null = null

function observeTileSlots() {
    if (!gameListRef.value) return

    const slots = gameListRef.value.querySelectorAll<HTMLElement>('[data-tile-index]')

    for (const slot of slots) {
        if (observer) {
            // observe() is idempotent per target, so keeping every slot observed
            // (instead of unobserving on first hit) lets videos unmount on exit.
            observer.observe(slot)
        }
        else {
            // No IntersectionObserver support: mount everything up front.
            const index = Number(slot.dataset.tileIndex)
            visibleTiles.add(index)
            mediaTiles.add(index)
        }
    }
}

const gameListRef = useTemplateRef('gameList')
const currentColumns = ref<number | null>(null)

function updateColumns() {
    if (!gameListRef.value || !props.trimDanglingTiles) {
        currentColumns.value = null
        return
    }

    currentColumns.value = getComputedStyle(gameListRef.value).gridTemplateColumns.split(' ').length
}

function handleObserverEntries(entries: IntersectionObserverEntry[]) {
    for (const entry of entries) {
        const index = Number((entry.target as HTMLElement).dataset.tileIndex)

        if (entry.isIntersecting) {
            // Mount the tile once and keep it mounted, so scrolling back never
            // flashes a skeleton.
            visibleTiles.add(index)
            // Mount the autoplay video only while the tile is near the viewport.
            mediaTiles.add(index)
        }
        else {
            // Release the video element once the tile scrolls out of range — frees
            // its decode pipeline and memory. The tile and poster image stay mounted.
            mediaTiles.delete(index)
        }
    }
}

onMounted(() => {
    // Use a default intersection observer, since the vueuse observer is too aggressive with the cleanup, causing some tiles not to be loaded
    if ('IntersectionObserver' in window) {
        observer = new IntersectionObserver(handleObserverEntries, { rootMargin: '200px' })
    }

    updateColumns()
    observeTileSlots()
})

onBeforeUnmount(() => {
    observer?.disconnect()
    observer = null
})

useResizeObserver(gameListRef, updateColumns)

function getLastCompleteRowEnd(cols: number, count: number) {
    let slots = 0
    let lastCompleteRowEnd = 0

    for (let i = 0; i < count; i++) {
        const game = props.games?.[i]
        const isVideo = isVideoTile(game, props.videoEnabled)

        slots += isVideo ? 2 : 1

        if (slots % cols === 0) {
            lastCompleteRowEnd = i + 1
        }
    }

    return lastCompleteRowEnd || count
}

const trimmedVisibleGamesCount = computed(() => {
    const cols = currentColumns.value

    if (!cols || !props.games?.length) return visibleGamesCount.value

    return getLastCompleteRowEnd(cols, visibleGamesCount.value)
})

if (import.meta.client) {
    watch([() => props.games, trimmedVisibleGamesCount], async () => {
        await nextTick()

        observeTileSlots()
    })
}
</script>

<template>
    <div>
        <div
            ref="gameList"
            class="game-list"
            :class="{
                'game-list--has-sidebar': props.hasSidebar,
            }"
            :style="{ '--max-columns': props.maxColumns }"
        >
            <div
                v-for="index in trimmedVisibleGamesCount"
                :key="props.games?.[index - 1]?.slug ?? `skeleton_${index}`"
                :data-tile-index="index - 1"
                class="game-list__tile-slot"
                :class="{
                    'game-list__tile-slot--video': isVideoTile(props.games?.[index - 1], props.videoEnabled),
                    'game-list__tile-slot--loaded': loadedTiles.has(index - 1),
                }"
                :style="{ '--game-index': getGameSetIndex(index - 1) }"
            >
                <GameTile
                    v-if="visibleTiles.has(index - 1) && props.games?.[index - 1]"
                    :game="(props.games[index - 1] as Game)"
                    :video-enabled="props.videoEnabled"
                    :eager="isEagerTile(index - 1)"
                    :load-media="mediaTiles.has(index - 1)"
                    @game-click="(game: Game) => emit('game-click', game)"
                    @loaded="loadedTiles.add(index - 1)"
                />

                <Transition name="skeleton-fade">
                    <div v-if="!loadedTiles.has(index - 1)" class="skeleton-item game-list__tile-skeleton" />
                </Transition>
            </div>
        </div>

        <div v-if="props.forceShowAllButton || props.showAllButton" class="game-list__action">
            <hr>
            <slot name="showAllUrl">
                <UIButton color="secondary" v-bind="props.showAllLink">
                    <!-- eslint-disable-next-line vue/no-v-html props.showAllText & translations.gameList.show_all is safe -->
                    <span v-html="props.showAllText || translations.gameList.show_all" />
                </UIButton>
            </slot>
            <hr>
        </div>

        <template v-if="currentGamesCount">
            <div
                v-if="props.showMoreButton"
                class="game-list__action game-list__action--load-more"
                :class="{ 'game-list--loading': props.loadingMore }"
            >
                <div v-if="gameListBarWidth > 0" class="game-list__states">
                    <div class="game-list__bar">
                        <span class="game-list__bar-progress" :style="`width: ${gameListBarWidth}%;`" />
                    </div>
                    <p>
                        {{
                            translations.gameList.games_displayed
                                .replace('{gamesDisplayed}', currentGamesCount?.toString())
                                .replace('{totalGames}', props.totalGames?.toString() ?? '')
                        }}
                    </p>
                </div>
                <UIButton
                    class="game-list__load-more-button"
                    color="secondary"
                    :loading="props.loadingMore"
                    :disabled="currentGamesCount === props.totalGames || props.loadingMore"
                    @click.prevent="loadMoreGames(category)"
                >
                    {{ translations.gameList.load_more }}
                </UIButton>
            </div>
        </template>
    </div>
</template>

<style lang="scss" scoped>
$ctaMinWidth: 212px;

.game-list {
    &--has-sidebar {
        position: relative;
    }

    &__action {
        display: flex;
        align-items: center;
        justify-content: center;
        place-items: center;
        margin-top: 30px;

        a,
        button {
            min-width: $ctaMinWidth;
            margin-right: 30px;
            margin-left: 30px;
            text-align: center;
            padding: 15px;
        }

        hr {
            flex-grow: 1;
        }

        &--load-more {
            flex-direction: column;
        }
    }

    &__states {
        position: relative;
        display: flex;
        text-align: center;
        align-items: center;
        justify-content: center;
        min-width: $ctaMinWidth;

        p {
            margin: 13px 0 10px;
        }
    }

    &__bar {
        width: 100%;
        background: $game-load-more-bar-bg; // Replace with variable in time

        &,
        &-progress {
            position: absolute;
            top: 0;
            left: 0;
            height: 3px;
        }

        &-progress {
            background: $game-load-more-progress-bg;
            transition: width 0.5s ease-out;
        }
    }

    &__load-more-button {
        min-height: 50px;

        &:disabled {
            display: none;
        }
    }

    &__tile-slot {
        position: relative;
        aspect-ratio: 1;

        &--video {
            grid-column: span 2;
            aspect-ratio: auto;
        }

        :deep(.game-tile .background) {
            opacity: 0;
            transition: opacity 0.2s ease-out;
        }

        &--loaded :deep(.game-tile .background) {
            opacity: 1;
        }
    }

    &__tile-skeleton {
        position: absolute;
        inset: 0;
        z-index: 2;
        aspect-ratio: auto;
    }
}

.skeleton-fade-leave-active {
    transition: opacity 0.3s ease-out;
}

.skeleton-fade-leave-to {
    opacity: 0;
}
</style>
